import { InputAdornment, TextField } from '@one-thd/sui-atomic-components';
import { Calendar } from '@one-thd/sui-icons';
import { DateTime } from 'luxon';
import React, { Dispatch, SetStateAction } from 'react';
import { DateFilterSettings, DateSettings } from '../../../models/order-filter';
import { validateDate } from '../../../utils/purchase-history/date-validation';

interface IOrderFilterInputProps {
  name: string;
  dateType: 'startDate' | 'endDate';
  dateSettings: DateSettings;
  setDateSettings: Dispatch<SetStateAction<DateSettings>>;
  setFilterSettings: Dispatch<SetStateAction<DateFilterSettings>>;
}

export const OrderFilterInput = ({
  name,
  dateType,
  dateSettings,
  setDateSettings,
  setFilterSettings,
}: IOrderFilterInputProps) => {
  const label = { inputProps: { 'aria-label': 'date range' } };

  const currentValue =
    dateSettings?.selectedDate === 'custom'
      ? (dateSettings[dateType] as string) ?? ''
      : '';

  const otherDateStr =
    dateType === 'endDate'
      ? dateSettings.startDate?.toString()
      : dateSettings.endDate?.toString();

  const errorMessage = validateDate(
    currentValue,
    otherDateStr ?? null,
    dateType,
  );

  return (
    <TextField
      placeholder="MM/DD/YYYY"
      id="basic-input"
      fullWidth
      status={errorMessage ? 'error' : undefined}
      statusMessage={errorMessage ?? undefined}
      value={currentValue}
      label={name}
      onBlur={() => {
        if (!currentValue) return;
        const parts = currentValue.split('/');
        if (parts.length !== 3) return;
        const [month, day, year] = parts;
        if (!month || !day || !year || year.length !== 4) return;
        const paddedMonth = month.padStart(2, '0');
        const paddedDay = day.padStart(2, '0');
        const padded = `${paddedMonth}/${paddedDay}/${year}`;
        if (padded === currentValue) return;
        setDateSettings((prevState) => ({
          ...prevState,
          [dateType]: padded,
          selectedDate: 'custom',
        }));
      }}
      onChange={(event: { target: { value: string } }) => {
        let inputDate = event.target.value.replace(/[^0-9/]/g, '');

        if (inputDate.length > 10) {
          inputDate = inputDate.slice(0, 10);
        }

        const slashCount = (inputDate.match(/\//g) || []).length;
        const prevLen = dateSettings[dateType]?.toString().length;
        const shouldAutoSlash =
          (inputDate.length === 2 && slashCount === 0 && prevLen !== 3) ||
          (inputDate.length === 5 && slashCount === 1 && prevLen !== 6);
        if (shouldAutoSlash) {
          inputDate += '/';
        }

        setDateSettings((prevState) => ({
          ...prevState,
          [dateType]: inputDate,
          selectedDate: 'custom',
        }));

        if (inputDate.length === 10) {
          const parsedDate = DateTime.fromFormat(inputDate, 'MM/dd/yyyy');

          if (parsedDate.isValid) {
            const month = parsedDate.month;
            const day = parsedDate.day;
            const year = parsedDate.year;

            const monthString = month < 10 ? `0${month}` : `${month}`;
            const dayString = day < 10 ? `0${day}` : `${day}`;

            setDateSettings((prevState) => ({
              ...prevState,
              selectedDate: 'custom',
              [dateType]: `${monthString}/${dayString}/${year}`,
            }));
          }
        }
        if (inputDate.length > 0) {
          setFilterSettings((prevState) => ({
            ...prevState,
            customDateUsed: true,
          }));
        }
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <span
              className="sui-cursor-pointer sui-flex"
              onClick={() => {
                if (dateType === 'startDate') {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    showStartCalendar: true,
                  }));
                } else if (dateType === 'endDate') {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    showEndCalendar: true,
                  }));
                }
              }}
              onKeyDown={() => {
                if (dateType === 'startDate') {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    showStartCalendar: true,
                  }));
                } else if (dateType === 'endDate') {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    showEndCalendar: true,
                  }));
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Open date picker"
            >
              <Calendar size="small" />
            </span>
          </InputAdornment>
        ),
        ...label,
      }}
    />
  );
};
